import { useState } from 'react';
import { UserPlusIcon, ChevronDownIcon, LinkIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { ShareDialog } from './ShareDialog';
import { useCurrentMap } from '../hooks/useCurrentMap';
// import { selectUserRoleForMap } from '../store/selectors';
import { RootState } from '../store/store';
import { useRoleWithOverride } from '../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../utils/getPermissionsForRole';
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../components_catalyst/dropdown';

export function ShareButton() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  // Check if current user can share this map
  const currentMap = useCurrentMap();
  const role = useRoleWithOverride(currentMap?.uuid || '');
  const { canShare } = getPermissionsForRole(role);

  // Only render ShareButton if user has sharing permissions
  if (!canShare) {
    return null;
  }

  // Calculate number of people the map is shared with
  const totalSharedPeople =
    (currentMap?.usersWithAccess?.length || 0) + (currentMap?.pendingMapInvitations?.length || 0);
  const sharedText =
    totalSharedPeople === 0
      ? 'Not shared'
      : totalSharedPeople === 1
        ? 'Shared with 1 person'
        : `Shared with ${totalSharedPeople} people`;

  // Generate role-aware tooltip
  const getTooltip = (userRole: string | null) => {
    switch (userRole) {
      case 'owner':
        return 'You are the owner. Invite collaborators.';
      case 'editor':
        return 'You are an editor. Invite collaborators.';
      default:
        return 'Share this map';
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsLinkCopied(true);
    setTimeout(() => setIsLinkCopied(false), 3000);
  };

  return (
    <aside aria-label="Share">
      <div className="flex">
        <button
          onClick={() => setIsDialogOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 cursor-pointer"
          title={getTooltip(role)}
        >
          <UserPlusIcon className="h-5 w-5" />
          <span>Share</span>
        </button>
        <Dropdown>
          {({ close }) => (
            <>
              <DropdownButton
                as="button"
                className="px-2 py-2 bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 cursor-pointer"
                title="Quick sharing actions"
              >
                <ChevronDownIcon className="h-5 w-5 text-gray-700" />
              </DropdownButton>

              <DropdownMenu anchor="bottom end" className="w-max">
                <DropdownItem
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    setIsLinkCopied(true);
                    setTimeout(() => {
                      setIsLinkCopied(false);
                      close();
                    }, 1500);
                  }}
                  className="cursor-pointer rounded-none hover:bg-gray-100 data-[focus]:bg-gray-100 data-[focus]:text-zinc-950 [&>[data-slot=icon]]:text-zinc-950 [&>[data-slot=icon]]:font-bold [&>[data-slot=icon]]:data-[focus]:text-zinc-950"
                >
                  <LinkIcon className="h-5 w-5" />
                  {isLinkCopied ? 'Link copied' : 'Copy link'}
                </DropdownItem>
                <DropdownDivider />
                <DropdownItem
                  disabled
                  className="cursor-default rounded-none text-gray-500 data-[disabled]:opacity-100 hover:bg-transparent data-[focus]:bg-transparent data-[focus]:text-gray-500"
                >
                  {sharedText}
                </DropdownItem>
              </DropdownMenu>
            </>
          )}
        </Dropdown>
      </div>

      <ShareDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
    </aside>
  );
}
